import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

const Terms = () => {
  useEffect(() => {
    document.title = "Terms of Service | VCapz Media";
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "June 2025";

  return (
    <div className="min-h-screen bg-background pt-28 pb-20">
      <article className="w-full max-w-3xl mx-auto px-4 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <FileText size={28} className="text-primary" />
          <h1 className="text-4xl md:text-5xl font-black text-white">Terms of Service</h1>
        </div>
        <p className="text-text-muted mb-12">Last updated: {lastUpdated}</p>

        <div className="prose-blog space-y-10">

          <section>
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing <strong>vcapz.com</strong> or engaging VCapz Media for any services, you agree to be bound by these Terms of Service. If you do not agree, please do not use our website or services.
            </p>
            <p>
              These Terms apply to all visitors, clients, and any other parties who access or use our services.
            </p>
          </section>

          <section>
            <h2>2. Services</h2>
            <p>VCapz Media provides the following services:</p>
            <ul>
              <li>Content creation (video production, photography, copywriting)</li>
              <li>Social media management and strategy</li>
              <li>Web design and development</li>
              <li>Brand strategy and campaign planning</li>
            </ul>
            <p>
              The specific scope, deliverables, timelines, and fees for any engagement are governed by a separate service agreement or proposal provided to the client prior to commencement.
            </p>
          </section>

          <section>
            <h2>3. Intellectual Property</h2>
            <h3>Our content</h3>
            <p>
              All content on this website — including text, graphics, logos, images, and code — is the property of VCapz Media and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our written consent.
            </p>
            <h3>Client deliverables</h3>
            <p>
              Upon full payment, clients receive the agreed usage rights to deliverables as specified in the service agreement. VCapz Media retains the right to display completed work in its portfolio unless otherwise agreed in writing.
            </p>
            <h3>Client-provided assets</h3>
            <p>
              Clients retain ownership of all assets, materials, and content they provide to VCapz Media for use in delivering services. By providing such assets, clients confirm they hold the necessary rights and licences to use them.
            </p>
          </section>

          <section>
            <h2>4. Payment Terms</h2>
            <ul>
              <li>Payment terms are specified in the individual service agreement or invoice</li>
              <li>A deposit may be required before work commences</li>
              <li>Late payments may result in paused delivery or suspension of services</li>
              <li>All fees are exclusive of applicable taxes unless stated otherwise</li>
            </ul>
          </section>

          <section>
            <h2>5. Revisions and Approvals</h2>
            <p>
              The number of revision rounds included in any engagement is defined in the service agreement. Additional revisions beyond the agreed scope may be subject to additional charges. Client approval at each stage is required before proceeding to the next.
            </p>
          </section>

          <section>
            <h2>6. Confidentiality</h2>
            <p>
              Both parties agree to keep confidential any proprietary or sensitive information shared during the course of an engagement. This obligation survives the termination of any agreement between the parties.
            </p>
          </section>

          <section>
            <h2>7. Limitation of Liability</h2>
            <p>
              VCapz Media shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services or website, including but not limited to loss of revenue, loss of data, or business interruption.
            </p>
            <p>
              Our total liability in connection with any service engagement shall not exceed the total fees paid by the client for the specific project giving rise to the claim.
            </p>
          </section>

          <section>
            <h2>8. Termination</h2>
            <p>
              Either party may terminate a service engagement with written notice as specified in the service agreement. Upon termination, the client is responsible for payment of all work completed up to the termination date. VCapz Media will deliver all completed assets upon receipt of final payment.
            </p>
          </section>

          <section>
            <h2>9. Website Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the website for any unlawful purpose</li>
              <li>Attempt to gain unauthorised access to any part of the website</li>
              <li>Transmit any malicious code or harmful content</li>
              <li>Scrape or harvest data from the site without permission</li>
            </ul>
          </section>

          <section>
            <h2>10. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising from these Terms or our services shall be subject to the exclusive jurisdiction of the courts in the relevant territory.
            </p>
          </section>

          <section>
            <h2>11. Changes to These Terms</h2>
            <p>
              We reserve the right to update these Terms at any time. Changes will be posted on this page with a revised date. Continued use of our website or services after changes are posted constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>For questions about these Terms, please contact:</p>
            <p>
              <strong>VCapz Media</strong><br />
              Email: <a href="mailto:mail@vcapz.com">mail@vcapz.com</a><br />
              Website: <a href="https://vcapz.com">vcapz.com</a>
            </p>
          </section>

        </div>

        <div className="mt-16 pt-10 border-t border-white/10">
          <Link to="/privacy" className="text-primary hover:underline text-sm font-semibold">
            Read our Privacy Policy →
          </Link>
        </div>
      </article>
    </div>
  );
};

export default Terms;
